import type { Assessment } from "../assessment-types";

/** 🫂 Relationships & Attachment — phase 1 expansion. */

export const FEAR_OF_ABANDONMENT_ASSESSMENT: Assessment = {
  slug: "fear-of-abandonment",
  emoji: "🫂",
  title: "Fear of Abandonment",
  tagline: "How separation, distance and rejection affect you",
  category: "relationships",
  keywords: ["abandonment", "rejection", "left", "clingy", "separation", "distance", "anxious attachment"],
  intro:
    "This reflects on how distance or the possibility of being left tends to feel for you. These responses often begin long before the relationships you're in now.",
  questions: [
    { id: "q1", text: "Do you feel uneasy when someone close takes longer than usual to reply?" },
    { id: "q2", text: "Do you look for signs that someone is pulling away?" },
    { id: "q3", text: "Does distance in a relationship feel physically uncomfortable?" },
    { id: "q4", text: "Do you find yourself trying harder when you sense someone withdrawing?" },
    { id: "q5", text: "Do you sometimes leave first to avoid being left?" },
    { id: "q6", text: "Do endings, even small ones, feel harder for you than for others?" },
    { id: "q7", text: "Do you need reassurance more often than you'd like to?" },
    { id: "q8", text: "Do you replay conversations looking for signs of rejection?" },
    { id: "q9", text: "Do you find it hard to feel secure even in steady relationships?" },
    { id: "q10", text: "Do you fear being 'too much' for the people you care about?" },
    { id: "q11", text: "Does time apart feel like something to get through?" },
    { id: "q12", text: "Would it help to feel steadier when someone you love is at a distance?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest separation doesn't unsettle you strongly at the moment.",
      body:
        "Almost everyone feels something when a close relationship goes quiet. Knowing your own threshold is helpful information.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "Your responses suggest that concerns around separation or rejection may sometimes affect you.",
      body:
        "This often develops where closeness was unpredictable. Learning to soothe the first wave of worry can create more room to respond rather than react.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "Your responses suggest fears about being left may be affecting you quite often.",
      body:
        "It can show up as reassurance-seeking, over-giving or withdrawing first. None of these mean something is wrong with you — they're protective strategies.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "Your responses suggest fear of abandonment may be a strong presence in your relationships.",
      body:
        "This is exhausting for you and often invisible to others. Support alongside steady, small practices of self-soothing can help this ease over time.",
    },
  ],
  explore: {
    understand: [
      { label: "Attachment styles", to: "/learn/attachment-styles" },
      { label: "Trauma and relationships", to: "/learn/trauma-relationships" },
    ],
    try: [
      { label: "Grounding exercises", to: "/grounding" },
      { label: "Self-love practices", to: "/self-love" },
    ],
    reflect: [
      { label: "Inner child reflections", to: "/inner-child" },
      { label: "Guided journal", to: "/guided-journal" },
    ],
  },
};

export const TRUST_AFTER_TRAUMA_ASSESSMENT: Assessment = {
  slug: "trust-after-trauma",
  emoji: "🤝",
  title: "Trust After Trauma",
  tagline: "How easy is it to trust yourself and other people?",
  category: "relationships",
  keywords: ["trust", "betrayal", "suspicion", "safety", "self-trust", "doubt"],
  intro:
    "This reflects on how trust feels for you now — both in other people and in your own judgement. Wariness after difficult experiences is protective, not paranoid.",
  questions: [
    { id: "q1", text: "Do you find it hard to believe people mean what they say?" },
    { id: "q2", text: "Do you wait for the moment when things go wrong?" },
    { id: "q3", text: "Do you doubt your own judgement about people?" },
    { id: "q4", text: "Do you hold back parts of yourself even with people close to you?" },
    { id: "q5", text: "Do you test people to see whether they'll stay?" },
    { id: "q6", text: "Do you find kindness hard to accept at face value?" },
    { id: "q7", text: "Do you replay past betrayals when someone new gets close?" },
    { id: "q8", text: "Do you feel safer when you rely only on yourself?" },
    { id: "q9", text: "Do you second-guess your instincts about a situation?" },
    { id: "q10", text: "Do you feel uneasy sharing something personal?" },
    { id: "q11", text: "Do you notice you scan for signs of dishonesty?" },
    { id: "q12", text: "Would you like to feel more able to trust your own read on things?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest trust feels reasonably available to you right now.",
      body:
        "Being able to trust selectively is a strength. Noticing what makes a relationship feel safe for you keeps that discernment sharp.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "Your responses suggest trust is sometimes difficult for you.",
      body:
        "After difficult experiences, caution is a sensible adaptation. Rebuilding trust usually happens gradually, in small, testable steps.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "Your responses suggest trusting others — or yourself — may often feel hard.",
      body:
        "Self-doubt frequently follows situations where your perception was dismissed. Reconnecting with your own signals is often as important as trusting anyone else.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "Your responses suggest trust may feel very difficult at the moment.",
      body:
        "That makes complete sense if trust has been broken before. Going slowly and choosing where to risk a little openness is a valid and protective approach.",
    },
  ],
  explore: {
    understand: [
      { label: "Trauma and relationships", to: "/learn/trauma-relationships" },
      { label: "Trauma bonds", to: "/learn/trauma-bonds" },
    ],
    try: [
      { label: "Grounding exercises", to: "/grounding" },
      { label: "Safety and stabilisation", to: "/safety-stabilisation" },
    ],
    reflect: [
      { label: "Know your patterns", to: "/know-your-patterns" },
      { label: "Guided journal", to: "/guided-journal" },
    ],
  },
};

export const RELATIONSHIP_SAFETY_ASSESSMENT: Assessment = {
  slug: "relationship-safety",
  emoji: "🚩",
  title: "Relationship Safety",
  tagline: "How safe, respected and free do you feel in relationships?",
  category: "relationships",
  keywords: ["safety", "red flags", "control", "respect", "abuse", "coercion", "walking on eggshells"],
  intro:
    "This reflects on how safe and free you feel with the people closest to you. If anything here worries you, support is available and you deserve it — this reflection is not a judgement of anyone.",
  questions: [
    { id: "q1", text: "Do you feel able to say what you think without fear of the response?" },
    { id: "q2", text: "Do you find yourself carefully managing someone's mood?" },
    { id: "q3", text: "Do you feel free to see friends and family?" },
    { id: "q4", text: "Do you feel your 'no' is respected?" },
    { id: "q5", text: "Do you feel criticised or belittled?" },
    { id: "q6", text: "Do you notice yourself walking on eggshells?" },
    { id: "q7", text: "Do you feel monitored, checked up on or controlled?" },
    { id: "q8", text: "Do you doubt your own memory of events after disagreements?" },
    { id: "q9", text: "Do you feel able to make decisions about your own money and time?" },
    { id: "q10", text: "Do you feel physically at ease around the person?" },
    { id: "q11", text: "Do you hide parts of your life to avoid conflict?" },
    { id: "q12", text: "Do you feel more yourself when you have time apart?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest your close relationships feel largely safe to you right now.",
      body:
        "It's still worth knowing what safety feels like in your body, so you notice quickly if that ever changes.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "Your responses suggest there may be moments where you feel less free or at ease.",
      body:
        "Occasional friction is normal in relationships. Persistent uneasiness or self-editing is worth paying attention to.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "Your responses suggest you may often be managing how safe you feel around someone.",
      body:
        "Adjusting yourself to keep the peace is a survival response, not a weakness. It may help to talk this through with someone you trust or a specialist support service.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "Your responses suggest safety and freedom may be significantly affected in a relationship.",
      body:
        "You deserve to feel safe. Please consider reaching out to a domestic-abuse or safeguarding service in your country, who can talk options through with you confidentially. If you are in immediate danger, contact emergency services.",
    },
  ],
  explore: {
    understand: [
      { label: "Abusive relationships", to: "/learn/abusive-relationships" },
      { label: "Boundaries", to: "/learn/boundaries" },
    ],
    try: [
      { label: "Safety and stabilisation", to: "/safety-stabilisation" },
      { label: "Crisis and urgent support", to: "/crisis" },
    ],
    reflect: [
      { label: "Abuse reflection journal", to: "/abuse-journal" },
      { label: "Know your patterns", to: "/know-your-patterns" },
    ],
  },
};
